import {type FC, Fragment, useEffect} from 'react';
import {useInView} from 'react-intersection-observer';
import {COLUMN_CRUMB, HOME_CRUMB} from '../../../../../config/constants/crumb';
import {HOME} from '../../../../../config/constants/routes';
import {useInfiniteGetColumnPostList} from '../../../../../core/repositories/columnRepository';
import {SpTopBottomBanner} from '../../../../../shared/components/Banner/SpTopBottomBanner';
import type {Breadcrumb} from '../../../../../shared/components/BreadcrumbsComponent';
import {Card, CardSkeleton} from '../Card';
import {Header} from '../Header';
import {COLUMNS_SPONSORED, COLUMNS_TOP} from '../routes';

const breadcrumbs: Breadcrumb[] = [
  {label: HOME_CRUMB, href: HOME},
  {label: COLUMN_CRUMB, href: COLUMNS_TOP},
  {
    label: '企業コラム',
    href: COLUMNS_SPONSORED,
  },
];

const title = '企業コラム';
const description =
  'さまざまな企業のコラムを掲載しています。興味がある企業の研究、就活対策に活用しましょう。';

export const SponsoredColumnListPage: FC = () => {
  const {ref: lastChunk, inView: lastIsInView} = useInView();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isInitialLoading,
    isFetchingNextPage,
    error,
  } = useInfiniteGetColumnPostList({
    queryParam: {
      type: 'nativeAd',
      limit: 30,
    },
  });

  useEffect(() => {
    if (lastIsInView && hasNextPage) {
      fetchNextPage();
    }
  }, [lastIsInView, hasNextPage, fetchNextPage]);

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={title}
        description={description}
      />
      <div className="w-full pc:w-[1200px] pc:mx-auto grid grid-cols-1 pc:grid-cols-3 mt-12 p-16 pc:px-0 gap-16 pc:gap-24">
        {data?.pages.map((page, outerIndex) => {
          if (!page.ok) return null;
          const firstPost = page.value.posts[0];
          if (!firstPost) return null;
          return (
            <Fragment key={firstPost.id}>
              {page.value.posts.map((post, innerIndex) => (
                <Fragment key={post.id}>
                  <Card
                    id={post.id}
                    title={post.title}
                    clipCount={post.favoriteCount}
                    isClipped={post.favorited}
                    tagNames={post.tags}
                    thumbnail={post.cardImageURL ?? post.carouselThumbnailURL}
                    className="w-full"
                    sponsoredOption={{
                      enabled: true,
                      companies: post.sponsorCompanies
                        ? post.sponsorCompanies
                        : [],
                    }}
                    updatedAt={post.updateDateTime}
                  />
                  {outerIndex === 0 && innerIndex === 0 && (
                    <div className="w-full flex pc:hidden justify-center pt-8 pb-16">
                      <SpTopBottomBanner type="top" />
                    </div>
                  )}
                  {outerIndex === 0 && innerIndex === 2 && (
                    <div className="w-full flex pc:hidden justify-center pt-8 pb-16">
                      <SpTopBottomBanner type="bottom" />
                    </div>
                  )}
                </Fragment>
              ))}
              {outerIndex === data.pages.length - 1 && (
                <div ref={lastChunk} className="w-full h-16" />
              )}
            </Fragment>
          );
        })}

        {(isInitialLoading || isFetchingNextPage) &&
          Array.from({length: 6}).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <CardSkeleton key={index} className="w-full" />
          ))}
      </div>

      {error && (
        <div className="flex justify-center py-24">
          <p className="text-error">データの取得に失敗しました</p>
        </div>
      )}
    </>
  );
};
